/**
 * VideoObject JSON-LD builder.
 *
 * Emits a single `<script type="application/ld+json">` VideoObject node so
 * Google can surface video rich results (thumbnail + duration + key moments)
 * for pages that embed a product/install video.
 */

import { absUrl, canonicalUrl, SITE_URL, SITE_NAME } from "@/lib/seo";

export interface VideoSchemaInput {
  /** Page path the video lives on, e.g. "/curtains". */
  path: string;
  /** Anchor id on the page for the video section (optional). */
  anchor?: string;
  name: string;
  description: string;
  /** Thumbnail image(s) — imported assets or paths. */
  thumbnails: (string | { src?: string })[];
  /** ISO-8601 duration, e.g. "PT5S". */
  duration?: string;
  /** ISO date the video was published. */
  uploadDate: string;
  /** Direct URL of the hosted media file (mp4). */
  contentUrl?: string;
  /** Player/embed page URL (e.g. the Facebook reel). */
  embedUrl?: string;
  /** Keywords for the clip. */
  keywords?: string;
  /** Actual creator when the clip was supplied by another organization. */
  creatorName?: string;
}

export function videoObjectScript(input: VideoSchemaInput) {
  const pageUrl = canonicalUrl(input.path);
  const id = `${pageUrl}#${input.anchor ?? "video"}`;

  const video: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": id,
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnails.map((t) => absUrl(t)),
    uploadDate: input.uploadDate,
    ...(input.duration ? { duration: input.duration } : {}),
    ...(input.contentUrl ? { contentUrl: absUrl(input.contentUrl) } : {}),
    ...(input.embedUrl ? { embedUrl: input.embedUrl } : {}),
    ...(input.keywords ? { keywords: input.keywords } : {}),
    isFamilyFriendly: true,
    inLanguage: "en-US",
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#business`,
      name: SITE_NAME,
    },
    creator: input.creatorName
      ? { "@type": "Organization", name: input.creatorName }
      : { "@id": `${SITE_URL}/#business` },
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    potentialAction: {
      "@type": "WatchAction",
      target: input.anchor ? `${pageUrl}#${input.anchor}` : pageUrl,
    },
  };

  return { type: "application/ld+json", children: JSON.stringify(video) };
}
